import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { eventService } from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import MessagesSidebar from '../components/MessagesSidebar';

const Events = () => {
    const navigate = useNavigate();
    const { language } = useLanguage();

    // Dropdown menu state
    const [activeMenu, setActiveMenu] = useState(null);
    const [activeSubMenu, setActiveSubMenu] = useState(null);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    // API state'leri
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [nextPage, setNextPage] = useState(null);
    const [previousPage, setPreviousPage] = useState(null);

    const limit = 20;

    const menuItems = [
        {
            id: 1,
            title: language === 'fa' ? 'ثبت نام در نمایشگاه' : 'Exhibition Registration',
            hasSubmenu: true,
            subItems: [
                language === 'fa' ? 'امور مالی' : 'Financial Affairs',
                language === 'fa' ? 'پیام ها' : 'Messages'
            ]
        },
        {
            id: 2,
            title: language === 'fa' ? 'حساب کاربری' : 'User Account',
            hasSubmenu: false
        },
        {
            id: 3,
            title: language === 'fa' ? 'پشتیبانی' : 'Support',
            hasSubmenu: false
        },
        {
            id: 4,
            title: language === 'fa' ? 'خروج از حساب کاربری' : 'Logout',
            hasSubmenu: false
        }
    ];

    useEffect(() => {
        fetchEvents(0);
    }, []);

    const fetchEvents = async (offset) => {
        setLoading(true);
        setError(null);

        try {
            const data = await eventService.getEvents(offset, limit);
            setEvents(data.results || []);
            setTotalCount(data.count || 0);
            setNextPage(data.next);
            setPreviousPage(data.previous);
            setCurrentPage(Math.floor(offset / limit));
        } catch (err) {
            console.error('Events API error:', err);
            setError(language === 'fa' ? 'خطا در دریافت رویدادها' : 'Error fetching events');
        } finally {
            setLoading(false);
        }
    };

    const handleNextPage = () => {
        if (nextPage) {
            const nextOffset = (currentPage + 1) * limit;
            fetchEvents(nextOffset);
        }
    };

    const handlePreviousPage = () => {
        if (previousPage) {
            const prevOffset = (currentPage - 1) * limit;
            fetchEvents(prevOffset);
        }
    };

    const handleMenuClick = (menuId) => {
        setActiveMenu(activeMenu === menuId ? null : menuId);
        setActiveSubMenu(null);
    };

    const handleSubMenuClick = (subIndex) => {
        setActiveSubMenu(subIndex);
    };

    // Mobile Menu


    // Loading
    if (loading && events.length === 0) {
        return (
            <div className="max-w-[1136px] mx-auto px-3 sm:px-4 py-8 font-iransansRegular">
                <div className="flex justify-center items-center py-20">
                    <div className="flex flex-col items-center gap-4">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 border-4 border-[#292DD8] border-t-transparent rounded-full animate-spin"></div>
                        <p className="text-[#292DD8] font-bold text-sm sm:text-lg">
                            {language === 'fa' ? 'در حال بارگذاری...' : 'Loading...'}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // Error
    if (error) {
        return (
            <div className="max-w-[1136px] mx-auto px-3 sm:px-4 py-8 font-iransansRegular">
                <div className="flex justify-center items-center py-20">
                    <div className="flex flex-col items-center gap-4 bg-red-50 rounded-xl p-6">
                        <svg className="w-10 h-10 sm:w-12 sm:h-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                        <p className="text-red-600 font-bold text-sm sm:text-lg">{error}</p>
                        <button onClick={() => fetchEvents(0)} className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm">
                            {language === 'fa' ? 'تلاش مجدد' : 'Try Again'}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const totalPages = Math.ceil(totalCount / limit);

    return (
        <div className="max-w-[1136px] mx-auto px-3 sm:px-4 py-4 lg:py-8 font-iransansRegular overflow-hidden">
            {/* Mobil Menü Toggle */}

            <div className='lg:hidden block'>

                <MessagesSidebar
                    activeKey=""
                    onNavigate={(key) => {
                        if (key === 'messages') navigate('/messages');
                        if (key === 'finance') navigate('/finance');
                        if (key === 'account') navigate('/account');
                        if (key === 'support') navigate('/support');
                        if (key === 'logout') navigate('/logout');
                    }}
                />
            </div>


            <div className="flex gap-6">
                {/* Desktop Dropdown Menü */}
                <div className="hidden lg:block w-[272px] shrink-0">
                    <MessagesSidebar
                        activeKey=""
                        onNavigate={(key) => {
                            if (key === 'messages') navigate('/messages');
                            if (key === 'finance') navigate('/finance');
                            if (key === 'account') navigate('/account');
                            if (key === 'support') navigate('/support');
                            if (key === 'logout') navigate('/logout');
                        }}
                    />
                </div>

                {/* Başlık ve Event Listesi */}
                <div className="flex-1 w-full">
                    <motion.h1
                        className="text-xl sm:text-2xl lg:text-[32px] font-extrabold text-[#292DD8] mb-6 lg:mb-8"
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        {language === 'fa' ? 'نمایشگاه‌ها و رویداد‌ها پیش رو' : 'Upcoming Exhibitions & Events'}
                    </motion.h1>

                    {/* Kartlar alt alta - grid yerine flex-col */}
                    <div className="flex flex-col gap-4 sm:gap-6">
                        {events.map((event, index) => (
                            <EventCard
                                key={event.slug}
                                event={event}
                                index={index}
                                language={language}
                                onClick={() => navigate(`/events/${event.slug}`)}
                                onRegister={() => navigate(`/events/${event.slug}`)}
                            />
                        ))}
                    </div>

                    {events.length === 0 && !loading && (
                        <div className="flex justify-center items-center py-20">
                            <p className="text-gray-500 text-sm sm:text-xl">
                                {language === 'fa' ? 'رویدادی یافت نشد' : 'No events found'}
                            </p>
                        </div>
                    )}

                    {totalPages > 1 && (
                        <div className="flex justify-center items-center gap-3 sm:gap-4 mt-6 lg:mt-8">
                            <button
                                onClick={handlePreviousPage}
                                disabled={!previousPage}
                                className={`px-3 py-2 sm:px-4 sm:py-2 rounded-lg font-bold text-xs sm:text-sm transition-all duration-300 ${previousPage
                                    ? 'bg-[#292DD8] text-white hover:bg-[#1f23a8] hover:shadow-lg'
                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    }`}
                            >
                                {language === 'fa' ? 'قبلی' : 'Previous'}
                            </button>

                            <span className="text-[#204286] font-bold text-xs sm:text-sm">
                                {language === 'fa'
                                    ? `صفحه ${currentPage + 1} از ${totalPages}`
                                    : `Page ${currentPage + 1} of ${totalPages}`
                                }
                            </span>

                            <button
                                onClick={handleNextPage}
                                disabled={!nextPage}
                                className={`px-3 py-2 sm:px-4 sm:py-2 rounded-lg font-bold text-xs sm:text-sm transition-all duration-300 ${nextPage
                                    ? 'bg-[#292DD8] text-white hover:bg-[#1f23a8] hover:shadow-lg'
                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    }`}
                            >
                                {language === 'fa' ? 'بعدی' : 'Next'}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Event Kart Component'i
const EventCard = ({ event, index, language, onClick, onRegister }) => {
    const cardRef = useRef(null);
    const isInView = useInView(cardRef, { once: true, amount: 0.2 });

    return (
        <motion.div
            ref={cardRef}
            onClick={onClick}
            className="relative w-full overflow-hidden shadow-lg group cursor-pointer rounded-2xl"
            style={{
                backgroundImage: `url(${event.banner})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                height: '164px',
                maxWidth: '835px',
            }}
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={isInView ? {
                opacity: 1,
                y: 0,
                scale: 1,
                transition: { duration: 0.6, delay: index * 0.1, ease: "easeOut" }
            } : {}}
            whileHover={{ scale: 1.02, transition: { duration: 0.3 } }}
        >

            <div className="absolute top-3 left-3 sm:top-4 sm:left-4 z-10">
                <span className={`px-2 py-0.5 sm:px-3 sm:py-1 rounded-full text-[10px] sm:text-xs font-bold text-white ${event.status === 1 ? 'bg-green-500' : 'bg-red-500'
                    }`}>
                    {event.status === 1
                        ? (language === 'fa' ? 'فعال' : 'Active')
                        : (language === 'fa' ? 'منقضی' : 'Expired')
                    }
                </span>
            </div>

            <motion.button
                onClick={onRegister}
                className="absolute bottom-3 right-3 sm:bottom-4 sm:right-4 z-10 px-4 py-1.5 sm:px-6 sm:py-2 rounded-lg text-white font-bold text-[10px] sm:text-sm shadow-lg
                    bg-gradient-to-r from-[#292DD8] to-[#4B4FEE]
                    hover:from-[#1f23a8] hover:to-[#3a3ecc]
                    transition-all duration-300"
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? {
                    opacity: 1,
                    y: 0,
                    transition: { delay: 0.3, duration: 0.4 }
                } : {}}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
            >
                {language === 'fa' ? 'مشاهده' : 'Show'}
            </motion.button>
        </motion.div>
    );
};

export default Events;